import { Link } from 'react-router-dom';
import { siteInfo, testimonials, comics } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

const breadcrumbItems = [
  { label: '首页', href: '/' },
  { label: '关于我们' },
];

const milestones = [
  { year: '2023', title: '平台创立', description: '073漫画网正式上线，致力于打造免费韩国漫画阅读平台' },
  { year: '2023', title: '首批合作', description: '与5家韩国漫画工作室达成合作，上线100+部漫画作品' },
  { year: '2024', title: '用户突破', description: '注册用户突破100万，月活跃用户达50万' },
  { year: '2024', title: '内容扩展', description: '漫画数量突破2000部，覆盖全部分类' },
  { year: '2025', title: '技术升级', description: '全新阅读器上线，支持高清画质和流畅翻页体验' },
  { year: '2026', title: '持续成长', description: '与20+韩国漫画工作室合作，累计收录3800+部漫画' },
];

const stats = [
  { value: '3,800+', label: '漫画作品' },
  { value: '20+', label: '合作工作室' },
  { value: '200万+', label: '注册用户' },
  { value: '99.8%', label: '服务可用率' },
];

export default function AboutPage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于我们 - 073漫画网',
            description: siteInfo.description,
            url: 'https://073manhua.com/about',
            about: {
              '@type': 'Organization',
              name: siteInfo.name,
              description: siteInfo.description,
              foundingDate: siteInfo.founded,
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer service',
                email: siteInfo.email,
                telephone: siteInfo.phone,
              },
              address: {
                '@type': 'PostalAddress',
                addressLocality: '广州',
                addressRegion: '广东',
                addressCountry: 'CN',
                streetAddress: siteInfo.address,
              },
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                { '@type': 'ListItem', position: 1, name: '首页', item: 'https://073manhua.com' },
                { '@type': 'ListItem', position: 2, name: '关于我们' },
              ],
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: '073漫画网上的漫画真的完全免费吗？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: '是的！073漫画网上的所有漫画作品完全免费阅读，无需付费，无需订阅。',
                },
              },
              {
                '@type': 'Question',
                name: '如何联系073漫画网？',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: `您可以通过邮箱${siteInfo.email}或电话${siteInfo.phone}联系我们。`,
                },
              },
            ],
          }),
        }}
      />

      <div className="pt-20 md:pt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-10">
          <Breadcrumb items={breadcrumbItems} />

          <section className="mt-8 mb-16">
            <span className="text-xs font-medium text-secondary-600 uppercase tracking-wider">About Us</span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 mt-2 leading-tight">
              关于
              <span className="text-primary-500">073漫画网</span>
            </h1>
            <div className="mt-8 max-w-3xl">
              <p className="text-foreground-600 text-base leading-relaxed">
                {siteInfo.description}
              </p>
              <p className="text-foreground-600 text-base leading-relaxed mt-4">
                {siteInfo.mission}
              </p>
            </div>
          </section>

          <section className="mb-16">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="bg-background-100 rounded-2xl p-6 text-center border border-background-200">
                  <p className="font-heading text-2xl md:text-3xl font-bold text-primary-500">{stat.value}</p>
                  <p className="text-foreground-500 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 -translate-x-1/2 hidden md:block" />
              <div className="space-y-8">
                {milestones.map((milestone, index) => (
                  <div
                    key={index}
                    className={`flex flex-col md:flex-row gap-4 md:gap-8 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                      <div className="bg-background-100 rounded-2xl p-5 md:p-6 border border-background-200">
                        <span className="text-xs font-semibold text-accent-500">{milestone.year}</span>
                        <h3 className="font-heading text-lg font-bold text-foreground-950 mt-1">{milestone.title}</h3>
                        <p className="text-foreground-500 text-sm mt-2 leading-relaxed">{milestone.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-center hidden md:flex">
                      <div className="w-4 h-4 rounded-full bg-accent-500 border-4 border-background-50" />
                    </div>
                    <div className="flex-1 hidden md:block" />
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10">
              读者口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-background-100 rounded-2xl p-6 border border-background-200">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-primary-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed">{t.quote}</p>
                  <div className="flex items-center gap-3 mt-4 pt-4 border-t border-background-200">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      {t.verified && (
                        <div className="flex items-center gap-1">
                          <i className="ri-verified-badge-fill text-secondary-500 text-xs"></i>
                          <span className="text-xs text-foreground-500">已验证读者</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-10">
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-xl text-primary-500"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">电子邮箱</h3>
                    <p className="text-foreground-500 text-sm mt-1">{siteInfo.email}</p>
                    <p className="text-foreground-400 text-xs mt-1">工作日24小时内回复</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-xl text-accent-500"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">客服电话</h3>
                    <p className="text-foreground-500 text-sm mt-1">{siteInfo.phone}</p>
                    <p className="text-foreground-400 text-xs mt-1">工作日 9:00-18:00</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-secondary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-xl text-secondary-500"></i>
                  </div>
                  <div>
                    <h3 className="font-heading text-base font-bold text-foreground-950">公司地址</h3>
                    <p className="text-foreground-500 text-sm mt-1">{siteInfo.address}</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-2xl p-6 border border-background-200">
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-4">发送消息</h3>
                <form className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">您的姓名</label>
                    <input
                      type="text"
                      name="name"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入姓名"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">电子邮箱</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入邮箱"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-foreground-600 mb-1 block">留言内容</label>
                    <textarea
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您想说的话..."
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-6 py-3 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors"
                  >
                    发送消息 <i className="ri-send-plane-line ml-1"></i>
                  </button>
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}